'use client';
import { isPayrollDiscoveryPathVisible } from '@/lib/payroll/payroll-experience-navigation';
import { resolveSettingsAccess } from '@/lib/settings/settings-resolver';
import { isCasesHrefVisible } from '@/lib/hr/cases-access';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { Command } from 'cmdk';
import { useRouter } from 'next/navigation';
import { createClient } from '../lib/supabase/client';
import { hrPageFenceKey } from '@/lib/workforce/hr-page-fence';
import { useCasualWorkerNavigation } from '@/hooks/useCasualWorkerNavigation';
import { useAuthStore } from '../stores/auth-store';
import { useModules } from '../contexts/module-context';
import { useOrgLabels } from '../contexts/org-labels-context';
import { isRoleAllowedForPath } from '../lib/route-access';
import { isModuleNavigationVisible } from '../lib/module-access';
import { isDashboardProductionSurfaceVisibleForTenant } from '../lib/production-surface-access';
import { isLaunchPrimaryNavigationVisible } from '../lib/launch-navigation';
import { isTenantNavigationPathVisible } from '../lib/navigation/tenant-navigation';
import { isFrontlineSelfServiceNavigationVisible } from '../lib/navigation/frontline-self-service';
import {
  filterShiftSwapNavigationItems,
  isShiftSwapNavigationPathVisible,
} from '../lib/navigation/shift-swap-navigation';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import {
  NAV_V2,
  flattenNavV2,
  type NavV2GroupId,
  type ProgramTag,
  type ProviderType,
} from '@/lib/navigation/nav-v2';
import {
  listNavGraphDeepLinks,
  navGraphGroupLabel,
  navGraphKeywordsByHref,
} from '@/lib/navigation/nav-graph';
import { iconForNavGraphDeepLink } from '@/lib/navigation/nav-graph-icons';
import { isHrefVisibleForProviderProgram } from '@/lib/navigation/program-route-policy';
import { findClusterForPath, flattenSettingsIa } from '@/components/nav-v2/settings/settings-ia';
import { useSettingsDelegation } from '@/hooks/useSettingsDelegation';
import { useSettingsHiddenLeaves } from '@/hooks/useSettingsHiddenLeaves';
import {
  appendRecent,
  parseScopedQuery,
  pruneRecents,
  scopeTitle,
} from '@/lib/navigation/cmdk-helpers';
import { trackNavV2Event } from '@/lib/nav-v2/telemetry';
import { isComplianceTrialModuleSet } from '@/lib/compliance-trial-access';
import { useGovernmentIntegrations } from '@/contexts/government-integration-context';
import { isGovernmentIntegrationPathVisible } from '@/lib/integrations/government/enablement-types';
import { useTimezone } from '@/hooks/useTimezone';
import { todayLocal } from '@/lib/dates';
import { loadCurrentProgramClientScope } from '@/lib/experience/current-program-client-scope';
import { programFamilyLabel } from '@/lib/navigation/provider-type';
import type { LucideIcon } from 'lucide-react';
import {
  Search,
  Users,
  Shield,
  FileText,
  FileSignature,
  Calendar,
  DollarSign,
  Settings,
  BarChart3,
  Stethoscope,
  AlertTriangle,
  Plus,
  Home,
  ClipboardList,
  Activity,
  Pill,
  Eye,
  Heart,
  ShieldAlert,
  ShieldCheck,
  Scale,
  FileWarning,
  Megaphone,
  Lock,
  ArrowUpCircle,
  Star,
  Database,
  Leaf,
  Landmark,
  HardHat,
  FlaskConical,
  Wrench,
  Bug,
  Brain,
  Receipt,
  CreditCard,
  Wallet,
  PiggyBank,
  TrendingUp,
  MapPin,
  Truck,
  ClipboardCheck,
  GraduationCap,
  Clock,
  ArrowLeftRight,
  BookOpen,
  Building2,
  MessageSquare,
  Globe,
  UserCog,
  Layers,
  Bell,
  BellDot,
  CalendarDays,
  Upload,
  KeyRound,
  Cog,
  HelpCircle,
  Ticket,
  CheckSquare,
  ListChecks,
  Workflow,
  UserPlus,
  LayoutDashboard,
  Gauge,
  Factory,
  Wifi,
  Video,
  ScrollText,
  Bed,
  DoorOpen,
  Utensils,
  Moon,
  PersonStanding,
  Gamepad2,
  BarChart2,
  LineChart,
  PieChart,
  Mail,
} from 'lucide-react';

// ─── Navigation item type ───────────────────────────────────
interface NavItem {
  label: string;
  href: string;
  icon: LucideIcon;
  group: string;
  /** Extra search keywords (not displayed, but matched by cmdk) */
  keywords?: string;
}

// Static navigation items — comprehensive coverage of all accessible pages
const NAV_ITEMS: NavItem[] = [
  // ── Core Pages ──────────────────────────────────────────
  { label: 'Overview', href: '/overview', icon: Home, group: 'Pages' },
  {
    label: 'Clients',
    href: '/clients',
    icon: Users,
    group: 'Pages',
    keywords: 'residents consumers',
  },
  { label: 'Care plans', href: '/care-plans', icon: ClipboardList, group: 'Pages' },
  { label: 'Assessments', href: '/assessments', icon: ClipboardCheck, group: 'Pages' },
  { label: 'Waitlist', href: '/waitlist', icon: UserPlus, group: 'Pages' },

  // ── Clinical ────────────────────────────────────────────
  {
    label: 'Clinical',
    href: '/clinical',
    icon: Stethoscope,
    group: 'Clinical',
    keywords: 'clinical care',
  },
  { label: 'Observations', href: '/observations', icon: Eye, group: 'Clinical' },
  { label: 'Medications', href: '/medications', icon: Pill, group: 'Clinical' },
  {
    label: 'Medication Administration',
    href: '/medications/mar',
    icon: Pill,
    group: 'Clinical',
    keywords: 'MAR',
  },
  {
    label: 'Medication Exceptions',
    href: '/medications/exceptions',
    icon: AlertTriangle,
    group: 'Clinical',
    keywords: 'missed refused withheld eMAR',
  },
  {
    label: 'Clinical Handovers',
    href: '/clinical/handovers',
    icon: ClipboardList,
    group: 'Clinical',
    keywords: 'ISBAR shift transfer acknowledgement',
  },
  {
    label: 'Overdue Assessments',
    href: '/clinical/overdue-assessments',
    icon: AlertTriangle,
    group: 'Clinical',
    keywords: 'assessment cadence due never done',
  },
  {
    label: 'Clinical Pathways',
    href: '/clinical/pathways',
    icon: Workflow,
    group: 'Clinical',
    keywords: 'deterioration falls delirium end of life wounds',
  },
  {
    label: 'Restorative Care',
    href: '/clinical/restorative',
    icon: Heart,
    group: 'Clinical',
    keywords: 'reablement rehabilitation maintenance allied health',
  },
  {
    label: 'Clinical Supervision',
    href: '/people/development/supervision',
    icon: ClipboardCheck,
    group: 'Clinical',
    keywords: 'rn en ain relationship competency supervision',
  },
  {
    label: 'S8 Register',
    href: '/medications/s8-register',
    icon: AlertTriangle,
    group: 'Clinical',
    keywords: 'schedule 8 controlled drugs',
  },
  { label: 'Wounds', href: '/wounds', icon: Activity, group: 'Clinical' },
  {
    label: 'Advance care plans',
    href: '/advance-care-plans',
    icon: ScrollText,
    group: 'Clinical',
    keywords: 'ACP directives',
  },

  // ── Residential ─────────────────────────────────────────
  { label: 'Residential Overview', href: '/residential', icon: Building2, group: 'Residential' },
  { label: 'Rooms & Beds', href: '/residential/rooms', icon: Bed, group: 'Residential' },
  { label: 'Admissions', href: '/residential/admissions', icon: DoorOpen, group: 'Residential' },
  { label: 'Occupancy', href: '/residential/occupancy', icon: BarChart2, group: 'Residential' },
  {
    label: 'Care Minutes',
    href: '/residential/care-minutes',
    icon: Clock,
    group: 'Residential',
    keywords: 'AN-ACC care minutes',
  },
  {
    label: '24/7 RN Coverage',
    href: '/compliance/rn-coverage',
    icon: Stethoscope,
    group: 'Residential',
    keywords: 'registered nurse coverage residential care minutes',
  },
  { label: 'Activities', href: '/residential/activities', icon: Gamepad2, group: 'Residential' },
  { label: 'Visitors', href: '/residential/visitors', icon: PersonStanding, group: 'Residential' },
  { label: 'Community', href: '/residential/community', icon: Megaphone, group: 'Residential' },
  { label: 'Night Rounds', href: '/residential/night-rounds', icon: Moon, group: 'Residential' },
  {
    label: 'Rounds',
    href: '/residential/rounds',
    icon: ClipboardCheck,
    group: 'Residential',
    keywords: 'resident rounds care checks',
  },
  {
    label: 'ADL Events',
    href: '/residential/adl-events',
    icon: Activity,
    group: 'Residential',
    keywords: 'activities of daily living personal care',
  },
  {
    label: 'Meal Management',
    href: '/residential/meal-management',
    icon: Utensils,
    group: 'Residential',
  },
  {
    label: 'DAP reconciliation',
    href: '/residential/dap-accrual',
    icon: DollarSign,
    group: 'Residential',
    keywords: 'daily accommodation payment finance accrual',
  },
  {
    label: 'Daily Living',
    href: '/residential/daily-living',
    icon: ClipboardCheck,
    group: 'Residential',
    keywords: 'ADL missed care personal care toileting repositioning',
  },

  // ── Home Care ───────────────────────────────────────────
  {
    label: 'Service Delivery',
    href: '/home-care/service-delivery',
    icon: Truck,
    group: 'Home Care',
    keywords: 'support at home SAH',
  },
  { label: 'Service Areas', href: '/home-care/service-areas', icon: MapPin, group: 'Home Care' },
  { label: 'Schedules', href: '/home-care/schedules', icon: Calendar, group: 'Home Care' },
  {
    label: 'Home Assessments',
    href: '/home-care/assessments',
    icon: ClipboardCheck,
    group: 'Home Care',
  },
  {
    label: 'Monthly Statements',
    href: '/home-care/statements',
    icon: Mail,
    group: 'Home Care',
    keywords: 'SAH statement s155-40 monthly',
  },
  {
    label: 'SAH Budgets',
    href: '/home-care/budgets',
    icon: Wallet,
    group: 'Home Care',
    keywords: 'support at home SAH budget allocation quarter remaining spend',
  },
  {
    label: 'Claims Export',
    href: '/home-care/claims',
    icon: FileText,
    group: 'Home Care',
    keywords: 'SAH claim batch CSV Services Australia PRODA',
  },
  {
    label: 'AT & Home Mods',
    href: '/home-care/at-hm',
    icon: Wrench,
    group: 'Home Care',
    keywords: 'assistive technology home modification wheelchair hoist ramp rail',
  },

  // ── Compliance ──────────────────────────────────────────
  {
    label: 'Compliance Hub',
    href: '/compliance',
    icon: ShieldCheck,
    group: 'Compliance',
    keywords: 'quality event improvement evidence audit pack regulator',
  },
  {
    label: 'My Compliance Work',
    href: '/compliance/work',
    icon: ListChecks,
    group: 'Compliance',
    keywords: 'worklist overdue due soon unassigned owner assignment deadlines',
  },
  {
    label: 'Compliance calendar',
    href: '/compliance/calendar',
    icon: ListChecks,
    group: 'Compliance',
    keywords: 'calendar due dates obligations statutory subscription ics',
  },
  {
    label: 'Care incidents',
    href: '/compliance/incidents',
    icon: Shield,
    group: 'Compliance',
    keywords: 'serious incident response scheme',
  },
  { label: 'Complaints', href: '/compliance/complaints', icon: Megaphone, group: 'Compliance' },
  {
    label: 'Quality Standards',
    href: '/compliance/quality-indicators',
    icon: ShieldCheck,
    group: 'Compliance',
    keywords: 'aged care quality standards',
  },
  {
    label: 'Quality Hub',
    href: '/compliance/standards',
    icon: ShieldCheck,
    group: 'Compliance',
    keywords: 'QI indicators',
  },
  {
    label: 'Consumer Feedback',
    href: '/compliance/complaints?tab=feedback',
    icon: Megaphone,
    group: 'Compliance',
  },
  {
    label: 'Stakeholder Engagement',
    href: '/compliance/standards?tab=engagement',
    icon: Users,
    group: 'Compliance',
  },
  // Worker Screening / Policies / Development / Evidence / Whistleblower —
  // served by NAV_V2 or nav-graph deep links (Cmd-K graph cutover).
  {
    label: 'Restrictive Practices',
    href: '/compliance/restrictive-practices',
    icon: Lock,
    group: 'Compliance',
    keywords: 'BSP behaviour support',
  },

  {
    label: 'Continuous improvement',
    href: '/compliance/continuous-improvement',
    icon: ArrowUpCircle,
    group: 'Compliance',
    keywords:
      'plan for continuous improvement PCI corrective action quality improvement suggestion PDSA CI',
  },
  {
    label: 'Review continuous improvement',
    href: '/compliance/continuous-improvement?view=review',
    icon: ArrowUpCircle,
    group: 'Compliance',
  },
  { label: 'Audit trail', href: '/compliance/audit-trail', icon: FileText, group: 'Compliance' },
  {
    label: 'Audit Program',
    href: '/compliance/audit-program',
    icon: ClipboardCheck,
    group: 'Compliance',
    keywords: 'accreditation self-assessment standards',
  },
  {
    label: 'Compliance Reports',
    href: '/compliance/reporting',
    icon: FileText,
    group: 'Compliance',
    keywords: 'DEX',
  },
  {
    label: 'DEX Reporting',
    href: '/compliance/reporting/dex',
    icon: FileText,
    group: 'Compliance',
    keywords: 'data exchange',
  },
  {
    label: 'SAH Evidence Oversight',
    href: '/compliance/sah-evidence',
    icon: ShieldCheck,
    group: 'Compliance',
    keywords: 'support at home AT-HM claim evidence regional compliance',
  },
  {
    label: 'Expiring Documents',
    href: '/compliance/documents/expiring',
    icon: FileWarning,
    group: 'Compliance',
  },
  { label: 'Governance', href: '/compliance/governance', icon: Landmark, group: 'Compliance' },
  {
    label: 'Risk Register',
    href: '/compliance/governance/risk-register',
    icon: AlertTriangle,
    group: 'Compliance',
    keywords: 'risk matrix',
  },
  {
    label: 'Skills Matrix',
    href: '/compliance/governance/skills-matrix',
    icon: GraduationCap,
    group: 'Compliance',
    keywords: 'governing body',
  },
  {
    label: 'Conflict of Interest',
    href: '/compliance/governance/conflicts',
    icon: Scale,
    group: 'Compliance',
  },
  {
    label: 'Clinical Governance',
    href: '/compliance/clinical-governance',
    icon: Stethoscope,
    group: 'Compliance',
  },
  {
    label: 'Responsible Persons',
    href: '/compliance/responsible-persons',
    icon: Users,
    group: 'Compliance',
  },
  {
    label: 'Registration',
    href: '/compliance/registration',
    icon: FileText,
    group: 'Compliance',
    keywords: 'provider registration',
  },
  { label: 'Star Ratings', href: '/compliance/star-ratings', icon: Star, group: 'Compliance' },
  {
    label: 'Star Ratings Methodology',
    href: '/compliance/star-ratings/methodology',
    icon: Scale,
    group: 'Compliance',
    keywords: 'internal quality score methodology',
  },
  {
    label: 'Data Breach',
    href: '/compliance/data-breach',
    icon: Database,
    group: 'Compliance',
    keywords: 'NDB notifiable data breach',
  },
  { label: 'Environmental', href: '/compliance/environmental', icon: Leaf, group: 'Compliance' },
  {
    label: 'Prudential Standards',
    href: '/compliance/prudential',
    icon: Landmark,
    group: 'Compliance',
    keywords: 'RAD DAP refundable deposit',
  },
  {
    label: 'Infection Prevention',
    href: '/compliance/ipc',
    icon: Bug,
    group: 'Compliance',
    keywords: 'IPC infection control outbreaks',
  },

  // ── WHS ─────────────────────────────────────────────────
  {
    label: 'WHS Overview',
    href: '/compliance/whs',
    icon: HardHat,
    group: 'WHS',
    keywords: 'workplace health safety',
  },
  { label: 'WHS Incidents', href: '/compliance/whs/incidents', icon: AlertTriangle, group: 'WHS' },
  { label: 'Hazard Register', href: '/compliance/whs/hazards', icon: AlertTriangle, group: 'WHS' },
  {
    label: 'Chemical Register',
    href: '/compliance/whs/chemicals',
    icon: FlaskConical,
    group: 'WHS',
    keywords: 'SDS MSDS',
  },
  { label: 'Equipment', href: '/compliance/whs/equipment', icon: Wrench, group: 'WHS' },
  { label: 'Emergency Drills', href: '/compliance/whs/drills', icon: ShieldAlert, group: 'WHS' },
  {
    label: 'Psychosocial Hazards',
    href: '/compliance/whs/psychosocial',
    icon: Brain,
    group: 'WHS',
  },

  // ── Finance ─────────────────────────────────────────────
  { label: 'Finance Overview', href: '/finance', icon: DollarSign, group: 'Finance' },
  { label: 'Invoices', href: '/finance/invoices', icon: Receipt, group: 'Finance' },
  { label: 'SAH Claims', href: '/home-care/claims', icon: CreditCard, group: 'Finance' },
  {
    label: 'SAH Reconciliation',
    href: '/home-care/reconciliation',
    icon: Wallet,
    group: 'Finance',
    keywords: 'fees co-contribution client contributions',
  },
  { label: 'Financial Reports', href: '/finance/reports', icon: FileText, group: 'Finance' },
  {
    label: 'SAH Statements',
    href: '/home-care/statements',
    icon: FileText,
    group: 'Finance',
    keywords: 'client statements participant statements',
  },
  {
    label: 'Aged Debtors',
    href: '/finance/reports/aged-debtors',
    icon: FileText,
    group: 'Finance',
  },
  {
    label: 'Reconciliation',
    href: '/finance/reports/reconciliation',
    icon: FileText,
    group: 'Finance',
  },
  {
    label: 'Budget Carry-Over',
    href: '/finance/budgets/carry-over',
    icon: PiggyBank,
    group: 'Finance',
    keywords: 'SAH unspent rollover',
  },
  {
    label: 'AN-ACC Funding',
    href: '/finance/an-acc',
    icon: TrendingUp,
    group: 'Finance',
    keywords: 'australian national aged care classification',
  },
  {
    label: 'Residential billing',
    href: '/finance/residential-billing',
    icon: Landmark,
    group: 'Finance',
    keywords: 'pension deduction centrelink dva resident split',
  },
  {
    label: 'Payer balances',
    href: '/finance/statements',
    icon: FileText,
    group: 'Finance',
    keywords: 'head provider brokerage payer statement balance',
  },
  {
    label: 'Invoice statements',
    href: '/finance/reports/statements',
    icon: FileText,
    group: 'Finance',
    keywords: 'client resident invoices payments date range',
  },
  {
    label: 'Statement delivery log',
    href: '/finance/reports/statements/dispatch',
    icon: FileText,
    group: 'Finance',
    keywords: 'client statement dispatch history',
  },
  {
    label: 'Respite billing',
    href: '/finance/respite-billing',
    icon: CalendarDays,
    group: 'Finance',
    keywords: 'residential respite fee advice absence billable days',
  },

  // ── Pricing ─────────────────────────────────────────────
  { label: 'Rate Cards', href: '/pricing/rate-cards', icon: DollarSign, group: 'Pricing' },
  {
    label: 'Agreements',
    href: '/pricing/agreements',
    icon: FileText,
    group: 'Pricing',
    keywords: 'service agreement',
  },

  // ── Workforce / People L1 leaves + demoted deep links come from
  // flattenNavV2() + listNavGraphDeepLinks() (nav-graph SSOT). Residual
  // roster/payroll utilities not yet on the graph remain here.
  {
    label: 'Timesheets',
    href: '/roster/timesheets',
    icon: Clock,
    group: 'Workforce',
    keywords: 'approve timesheets approve hours',
  },
  {
    label: 'Worker queries',
    href: '/workforce/timesheets/disputes',
    icon: Clock,
    group: 'Workforce',
    keywords: 'timesheet disputes disputes',
  },
  {
    label: 'Pay period checks',
    href: '/roster/timesheets/reconciliation',
    icon: Clock,
    group: 'Workforce',
    keywords: 'period close ledger reconciliation',
  },
  {
    label: 'Time evidence',
    href: '/roster/time-review',
    icon: Clock,
    group: 'Workforce',
    keywords: 'time review confirm the day missing time overdue clock',
  },
  {
    label: 'Visit exceptions',
    href: '/roster/visit-exceptions',
    icon: CalendarDays,
    group: 'Workforce',
    keywords: 'confirm attended no-show forgotten clock visit close',
  },
  {
    label: 'Payroll Period Readiness',
    href: '/payroll/readiness',
    icon: ClipboardList,
    group: 'Workforce',
    keywords: 'payroll period readiness snapshot pay context create zero-pay closeout',
  },
  {
    label: 'Payroll Policy',
    href: '/payroll/policy',
    icon: ClipboardList,
    group: 'Workforce',
    keywords: 'payroll policy approve propose ordinary pattern exception part-time maker checker',
  },
  { label: 'Leave Management', href: '/roster/leave', icon: CalendarDays, group: 'Workforce' },
  { label: 'Shift swaps', href: '/roster/shift-swaps', icon: ArrowLeftRight, group: 'Workforce' },
  { label: 'Handovers', href: '/roster/handovers', icon: BookOpen, group: 'Workforce' },

  // ── Monitoring ──────────────────────────────────────────
  {
    label: 'Monitoring Overview',
    href: '/monitoring',
    icon: Wifi,
    group: 'Monitoring',
    keywords: 'IoT devices',
  },
  { label: 'IoT Devices', href: '/monitoring/devices', icon: Wifi, group: 'Monitoring' },
  { label: 'Monitoring Alerts', href: '/monitoring/alerts', icon: Bell, group: 'Monitoring' },
  { label: 'Vital Signs', href: '/monitoring/vital-signs', icon: Activity, group: 'Monitoring' },
  {
    label: 'AT-HM Equipment',
    href: '/monitoring/equipment',
    icon: Wrench,
    group: 'Monitoring',
    keywords: 'assistive technology',
  },

  // ── Telehealth ──────────────────────────────────────────
  { label: 'Telehealth sessions', href: '/telehealth', icon: Video, group: 'Telehealth' },
  {
    label: 'Telehealth Schedule',
    href: '/telehealth/schedule',
    icon: Calendar,
    group: 'Telehealth',
  },
  {
    label: 'Telehealth Recordings',
    href: '/telehealth/recordings',
    icon: Video,
    group: 'Telehealth',
  },

  // ── Analytics ───────────────────────────────────────────
  { label: 'Analytics', href: '/analytics', icon: PieChart, group: 'Analytics' },
  { label: 'Reports', href: '/reports', icon: BarChart3, group: 'Analytics' },
  { label: 'Benchmarking', href: '/analytics/benchmarking', icon: BarChart2, group: 'Analytics' },
  { label: 'Board pack', href: '/analytics/board-pack', icon: FileText, group: 'Analytics' },

  // ── Dashboards ──────────────────────────────────────────
  { label: 'All Dashboards', href: '/dashboards', icon: LayoutDashboard, group: 'Dashboards' },
  {
    label: 'Executive view',
    href: '/dashboards/executive',
    icon: BarChart3,
    group: 'Dashboards',
  },
  {
    label: 'Clinical Dashboard (DON)',
    href: '/dashboards/don-clinical',
    icon: Stethoscope,
    group: 'Dashboards',
  },
  {
    label: 'Compliance dashboard',
    href: '/dashboards/compliance',
    icon: Shield,
    group: 'Dashboards',
  },
  {
    label: 'Quality Dashboard',
    href: '/dashboards/quality',
    icon: ShieldCheck,
    group: 'Dashboards',
  },
  { label: 'Workforce Dashboard', href: '/dashboards/workforce', icon: Users, group: 'Dashboards' },
  {
    label: 'Finance dashboard',
    href: '/dashboards/finance',
    icon: DollarSign,
    group: 'Dashboards',
  },
  {
    label: 'Enterprise operations',
    href: '/dashboards/operations',
    icon: Gauge,
    group: 'Dashboards',
  },
  {
    label: 'Home Care Operations',
    href: '/dashboards/home-care-ops',
    icon: Truck,
    group: 'Dashboards',
  },
  {
    label: 'Facility Operations',
    href: '/dashboards/facility-ops',
    icon: Factory,
    group: 'Dashboards',
  },
  { label: 'My Caseload', href: '/dashboards/caseload', icon: Users, group: 'Dashboards' },
  {
    label: 'Deadlines heatmap',
    href: '/compliance/calendar?view=agenda',
    icon: Clock,
    group: 'Dashboards',
  },
  {
    label: "Today's huddle",
    href: '/dashboards/huddle',
    icon: LayoutDashboard,
    group: 'Dashboards',
  },
  {
    label: 'Multi-Facility View',
    href: '/dashboards/multi-facility',
    icon: Building2,
    group: 'Dashboards',
  },
  {
    label: 'Risk Correlation',
    href: '/dashboards/risk-correlation',
    icon: LineChart,
    group: 'Dashboards',
  },
  { label: 'IoT Dashboard', href: '/dashboards/iot', icon: Wifi, group: 'Dashboards' },

  // ── Tasks ───────────────────────────────────────────────
  { label: 'My tasks', href: '/tasks', icon: CheckSquare, group: 'Tasks' },
  { label: 'All tasks', href: '/tasks/all', icon: ListChecks, group: 'Tasks' },
  {
    label: 'Documents and Signatures',
    href: '/tasks/signatures',
    icon: FileSignature,
    group: 'Tasks',
    keywords: 'esignatures documents signing queue delivery',
  },

  // ── Settings ────────────────────────────────────────────
  { label: 'Settings', href: '/settings', icon: Settings, group: 'Settings' },
  {
    label: 'Organisation',
    href: '/settings/organisation',
    icon: Building2,
    group: 'Settings',
    keywords: 'org details ABN',
  },
  { label: 'Facilities', href: '/settings/facilities', icon: Building2, group: 'Settings' },
  {
    label: 'Facility Configuration',
    href: '/settings/facility-config',
    icon: Building2,
    group: 'Settings',
  },
  {
    label: 'Registration & Providers',
    href: '/settings/registration-providers',
    icon: Layers,
    group: 'Settings',
  },
  { label: 'Users & Roles', href: '/settings/users', icon: UserCog, group: 'Settings' },
  {
    label: 'Approval Rules',
    href: '/settings/approvals',
    icon: ListChecks,
    group: 'Settings',
  },
  { label: 'Modules', href: '/settings/modules', icon: Layers, group: 'Settings' },
  {
    label: 'Workflow Templates',
    href: '/settings/workflows',
    icon: Workflow,
    group: 'Settings',
  },
  {
    label: 'Escalation Policies',
    href: '/settings/escalations',
    icon: AlertTriangle,
    group: 'Settings',
  },
  {
    label: 'Funding Authorisations',
    href: '/settings/funding-authorisations',
    icon: Landmark,
    group: 'Settings',
    keywords: 'funder caps purchase orders',
  },
  {
    label: 'Document Templates',
    href: '/settings/document-templates',
    icon: FileText,
    group: 'Settings',
    keywords: 'hr onboarding agreements employment templates',
  },
  {
    label: 'Onboarding Settings',
    href: '/settings/onboarding',
    icon: ClipboardList,
    group: 'Settings',
    keywords: 'worker client onboarding settings packs documents screening readiness consents',
  },
  {
    label: 'Client Onboarding Packs',
    href: '/settings/onboarding/client-packs',
    icon: ClipboardList,
    group: 'Settings',
    keywords: 'client packs consents residential support at home',
  },
  {
    label: 'Client Readiness Policy',
    href: '/settings/onboarding/readiness',
    icon: ClipboardList,
    group: 'Settings',
    keywords: 'readiness gate delivery evidence',
  },
  {
    label: 'Worker Step Library',
    href: '/settings/onboarding/worker/base',
    icon: ListChecks,
    group: 'Settings',
    keywords: 'onboarding steps worker statutory floor',
  },
  {
    label: 'Roster Settings',
    href: '/settings/roster',
    icon: ClipboardCheck,
    group: 'Settings',
    keywords: 'roster adoption mode backfill worker compliance attestations',
  },
  {
    label: 'Worker Onboarding Packs',
    href: '/settings/onboarding-packs',
    icon: ClipboardList,
    group: 'Settings',
    keywords:
      'hr staff worker onboarding packs create customise customize training documents policies',
  },
  {
    label: 'Cert Allowance Registry',
    href: '/settings/cert-allowance-registry',
    icon: ShieldCheck,
    group: 'Settings',
    keywords: 'first aid allowance certification credentials registry',
  },
  {
    label: 'CM Task Types',
    href: '/settings/cm-task-types',
    icon: ClipboardList,
    group: 'Settings',
    keywords: 'care management billable task types pooled cm catalogue',
  },
  {
    label: 'Signature Templates',
    href: '/settings/signature-templates',
    icon: FileText,
    group: 'Settings',
    keywords: 'esignatures service agreements consents templates',
  },
  { label: 'Portal Settings', href: '/settings/portal', icon: Globe, group: 'Settings' },
  { label: 'Notifications', href: '/settings/notifications', icon: Bell, group: 'Settings' },
  {
    label: 'Notification Routing',
    href: '/settings/notification-routing',
    icon: Workflow,
    group: 'Settings',
    keywords: 'alerts recipients on-call routing',
  },
  {
    label: 'Notification Preferences',
    href: '/settings/notification-preferences',
    icon: BellDot,
    group: 'Settings',
  },
  { label: 'Public Holidays', href: '/settings/holidays', icon: CalendarDays, group: 'Settings' },
  { label: 'Data Import', href: '/settings/import', icon: Upload, group: 'Settings' },
  {
    label: 'Data Management',
    href: '/settings/data-management',
    icon: Database,
    group: 'Settings',
    keywords: 'export deletion privacy',
  },
  {
    label: 'Retention Policy',
    href: '/settings/retention',
    icon: Database,
    group: 'Settings',
  },
  { label: 'Security', href: '/settings/security', icon: KeyRound, group: 'Settings' },
  {
    label: 'Rate Cards',
    href: '/pricing/rate-cards',
    icon: DollarSign,
    group: 'Settings',
    keywords: 'pricing billable charges',
  },
  {
    label: 'Service Agreements',
    href: '/pricing/agreements',
    icon: FileText,
    group: 'Settings',
    keywords: 'pricing client agreements',
  },
  {
    label: 'Enterprise Agreements',
    href: '/settings/payroll/eba',
    icon: DollarSign,
    group: 'Settings',
    keywords: 'payroll eba award',
  },
  {
    label: 'Xero Mapping Wizard',
    href: '/settings/integrations/xero/wizard',
    icon: Layers,
    group: 'Settings',
    keywords: 'xero payroll mapping dry-run',
  },
  {
    label: 'Billing',
    href: '/settings/billing',
    icon: CreditCard,
    group: 'Settings',
    keywords: 'subscription plan payment',
  },
  { label: 'System Settings', href: '/settings/system', icon: Cog, group: 'Settings' },
  {
    label: 'Tenant Config Browser',
    href: '/admin/system/tenant-config',
    icon: FileText,
    group: 'Settings',
    keywords: 'tenant configs readonly',
  },
  {
    label: 'Master Data',
    href: '/admin/system/master-data',
    icon: Database,
    group: 'Settings',
    keywords: 'mdm service catalogues award rates public holidays classifications',
  },
  {
    label: 'AI Usage',
    href: '/admin/system/ai-usage',
    icon: Database,
    group: 'Settings',
    keywords: 'ai audit logs usage',
  },
  {
    label: 'Signature Corrections',
    href: '/admin/system/signature-corrections',
    icon: FileSignature,
    group: 'Settings',
    keywords: 'signature blocks revoke supersede evidence correction',
  },
  {
    label: 'Government connections',
    href: '/settings/integrations',
    icon: Layers,
    group: 'Settings',
  },

  // ── Help ────────────────────────────────────────────────
  { label: 'Help Centre', href: '/help', icon: HelpCircle, group: 'Help' },
  { label: 'Support Tickets', href: '/help/tickets', icon: Ticket, group: 'Help' },

  // ── My Workspace ────────────────────────────────────────
  { label: 'My profile', href: '/me/profile', icon: Users, group: 'My Workspace' },
  { label: 'My roster', href: '/me/roster', icon: Calendar, group: 'My Workspace' },
  { label: 'My shift offers', href: '/me/shift-offers', icon: Bell, group: 'My Workspace' },
  { label: 'My Swaps', href: '/me/shift-swaps', icon: ArrowLeftRight, group: 'My Workspace' },
  { label: 'My leave', href: '/me/leave', icon: FileText, group: 'My Workspace' },
  { label: 'My Timesheets', href: '/me/timesheets', icon: Clock, group: 'My Workspace' },
  { label: 'Staff Messages', href: '/me/messages', icon: MessageSquare, group: 'My Workspace' },
  {
    label: 'My work readiness',
    href: '/me/work-readiness',
    icon: ClipboardCheck,
    group: 'My Workspace',
    keywords: 'onboarding training policies screening credentials renewals performance supervision',
  },

  // ── Other ───────────────────────────────────────────────
  { label: 'Translate', href: '/translate', icon: BookOpen, group: 'Pages' },
];

/** Real static Cmd-K projection after tenant feature-switch filtering. */
export function listStaticCommandPaletteItems(options: {
  shiftSwapsEnabled?: boolean;
}): readonly NavItem[] {
  return filterShiftSwapNavigationItems(NAV_ITEMS, options.shiftSwapsEnabled);
}

const QUICK_ACTIONS = [
  {
    label: 'New Client',
    href: '/clients/new',
    icon: Plus,
    group: 'Quick Actions',
  },
  {
    label: 'New Incident',
    href: '/compliance/incidents/new',
    icon: Plus,
    group: 'Quick Actions',
  },
  {
    label: 'New Complaint',
    href: '/compliance/complaints/new',
    icon: Plus,
    group: 'Quick Actions',
  },
];

const SAH_GO_LIVE_COMMAND_HREFS = [
  '/tasks',
  '/clients',
  '/home-care/service-delivery',
  '/home-care/readiness',
  '/home-care/budgets',
  '/home-care/claims',
  '/home-care/statements',
  '/care-plans',
] as const;

const RAC_GO_LIVE_COMMAND_HREFS = [
  '/tasks',
  '/clients',
  '/residential',
  '/residential/admissions',
  '/residential/care-minutes',
  '/residential/care-minutes/an-acc',
  '/dashboards/workforce',
  '/dashboards/quality',
] as const;

function goLiveCommandHrefs(providerType: ProviderType): readonly string[] {
  if (providerType === 'SAH') return SAH_GO_LIVE_COMMAND_HREFS;
  if (providerType === 'RAC') return RAC_GO_LIVE_COMMAND_HREFS;
  return [];
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const casualWorker = useCasualWorkerNavigation(open);
  // Shell a11y (Tier-2): focus-trap the open dialog + restore focus to the
  // element that opened it when it closes.
  const dialogRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const [search, setSearch] = useState('');
  const [clients, setClients] = useState<Array<{ id: string; name: string; programLabel: string }>>(
    [],
  );
  const [staff, setStaff] = useState<Array<{ id: string; name: string }>>([]);
  const router = useRouter();
  const user = useAuthStore((s) => s.user);
  const {
    location,
    locations,
    providerType,
    activeProgramFamily,
    providerProgramContext,
    programContextRevision,
    programSelectionRequired,
  } = useOrgLabels();
  const timezone = useTimezone();

  // Org-type-aware label rewrites for nav items where the static label is
  // residential-leaning (Facilities, Facility Ops, Multi-Facility). HC tenants
  // see Offices / Office Ops / Multi-Office.
  const NAV_LABEL_OVERRIDES: Record<string, string> = {
    '/settings/facilities': locations,
    '/dashboards/facility-ops': `${location} operations`,
    '/dashboards/multi-facility': `Multi-${location.toLowerCase()} dashboard`,
  };
  const applyOrgLabel = (item: NavItem): NavItem => {
    const override = NAV_LABEL_OVERRIDES[item.href];
    return override ? { ...item, label: override } : item;
  };

  // Module-aware filtering — hide items from modules not in the tenant's plan
  const {
    billingPlan,
    enabledModules,
    tenantId,
    isDemoTenant,
    visibleGoLiveScope,
    tenantHiddenNavigationPaths,
    frontlineSelfServiceOnly,
    shiftSwapsEnabled,
    payrollRebuildOn,
  } = useModules();
  const isComplianceTrial = isComplianceTrialModuleSet(enabledModules);
  const governmentIntegrations = useGovernmentIntegrations();
  const isModEnabled = (path: string) =>
    isModuleNavigationVisible(hrPageFenceKey(path), enabledModules);
  const isNavigationCuratedVisible = (path: string) =>
    isPayrollDiscoveryPathVisible(path, payrollRebuildOn) &&
    (path !== '/me/casual-conversion' || casualWorker) &&
    isLaunchPrimaryNavigationVisible(path, visibleGoLiveScope, { isDemoTenant }) &&
    isTenantNavigationPathVisible(path, tenantHiddenNavigationPaths) &&
    isShiftSwapNavigationPathVisible(path, shiftSwapsEnabled) &&
    isFrontlineSelfServiceNavigationVisible({
      href: path,
      role: user?.role,
      enabled: frontlineSelfServiceOnly === true,
    });
  const {
    data: hiddenSettingsHrefs,
    isPending: hiddenSettingsLoading,
    isError: hiddenSettingsError,
  } = useSettingsHiddenLeaves();
  const {
    data: settingsDelegation,
    isPending: settingsDelegationLoading,
    isError: settingsDelegationError,
  } = useSettingsDelegation();

  // Role-aware filtering — use the same route access evaluator as the
  // dashboard shell so discovery matches actual page-level access.
  const isRoleAllowed = (path: string) =>
    isRoleAllowedForPath(path, user?.role, billingPlan) && isCasesHrefVisible(path, user?.role);
  const isPathAllowedForDiscovery = useCallback(
    (path: string) => {
      const settingsMatch = findClusterForPath(path);
      if (!settingsMatch) return isRoleAllowed(path);
      // Settings IA lists some operational People/Care hosts as cross-section
      // settings leaves (narrower cohort). Cmd-K discovery must match page RBAC
      // for those operator surfaces — otherwise COMPLIANCE_OFFICER loses
      // screening after the graph cutover (settings leaf shadows graph).
      if (
        path.startsWith('/people/') ||
        path.startsWith('/home-care/') ||
        path.startsWith('/roster/') ||
        path.startsWith('/clinical/')
      ) {
        return isRoleAllowed(path);
      }
      if (
        hiddenSettingsLoading ||
        settingsDelegationLoading ||
        hiddenSettingsError ||
        settingsDelegationError
      )
        return false;
      return resolveSettingsAccess({
        leafId: path,
        actor: { role: user?.role },
        delegation: settingsDelegation,
        context: { hiddenHrefs: hiddenSettingsHrefs, billingPlan },
      }).visible;
    },
    [
      hiddenSettingsHrefs,
      hiddenSettingsLoading,
      hiddenSettingsError,
      settingsDelegation,
      settingsDelegationLoading,
      settingsDelegationError,
      billingPlan,
      user?.role,
    ],
  );
  const isLaunchSurfaceVisible = (path: string) =>
    isDashboardProductionSurfaceVisibleForTenant(path, {
      tenantId,
      isDemo: isDemoTenant,
    });
  const isCommandPaletteSurfaceEnabled = (item: { href: string }) =>
    isLaunchSurfaceVisible(item.href) &&
    isModEnabled(item.href) &&
    isGovernmentIntegrationPathVisible(item.href, governmentIntegrations, user?.role);
  const isProgramVisible = (item: { href: string; programs?: readonly ProgramTag[] }) =>
    isHrefVisibleForProviderProgram(
      item.href,
      providerProgramContext ?? providerType,
      item.programs,
      tenantId,
    );

  // Q8 union + D5 graph cutover: sidebar stays curated NAV_V2; Cmd-K exposes
  // (v2 L1) ∪ (settings IA) ∪ (nav-graph deep links) ∪ (residual NAV_ITEMS).
  // Discovery uses the same program predicate as the sidebar plus conservative
  // path classification for graph/settings/residual entries.
  const flatV2 = useMemo(() => flattenNavV2(), []);
  const v2Hrefs = useMemo(() => new Set(flatV2.map((i) => i.href)), [flatV2]);
  const graphKeywords = useMemo(
    () => navGraphKeywordsByHref({ shiftSwapsEnabled }),
    [shiftSwapsEnabled],
  );
  const graphDeepLinks = useMemo(
    () => listNavGraphDeepLinks({ shiftSwapsEnabled, role: user?.role ?? null }),
    [shiftSwapsEnabled, user?.role],
  );
  const graphDeepHrefs = useMemo(
    () => new Set(graphDeepLinks.map((n) => n.href)),
    [graphDeepLinks],
  );
  const flatSettingsIa = useMemo(() => flattenSettingsIa(), []);
  const settingsIaHrefs = useMemo(
    () => new Set(flatSettingsIa.map((item) => item.href)),
    [flatSettingsIa],
  );
  const staticNavItems = useMemo(
    () => listStaticCommandPaletteItems({ shiftSwapsEnabled }),
    [shiftSwapsEnabled],
  );

  const allAvailable = useMemo(() => {
    type UnionItem = {
      label: string;
      href: string;
      icon: LucideIcon;
      group: string;
      groupId: NavV2GroupId | null;
      keywords?: string;
      origin: 'v2' | 'graph' | 'v1';
    };
    const out: UnionItem[] = [];
    for (const it of flatV2) {
      if (
        isCommandPaletteSurfaceEnabled(it) &&
        isProgramVisible(it) &&
        isNavigationCuratedVisible(it.href) &&
        isPathAllowedForDiscovery(it.href)
      ) {
        out.push({
          label: it.label,
          href: it.href,
          icon: it.icon,
          group: it.groupTitle,
          groupId: it.groupId,
          keywords: graphKeywords.get(it.href),
          origin: 'v2',
        });
      }
    }
    for (const it of flatSettingsIa) {
      if (v2Hrefs.has(it.href)) continue;
      // Cross-section People operator hosts are discovered via NAV_V2 / graph
      // with page RBAC — not the narrower settings-rail cohort.
      if (it.href.startsWith('/people/')) continue;
      if (
        isCommandPaletteSurfaceEnabled(it) &&
        isProgramVisible(it) &&
        isNavigationCuratedVisible(it.href) &&
        isPathAllowedForDiscovery(it.href)
      ) {
        out.push({
          label: it.label,
          href: it.href,
          icon: it.icon,
          group: it.clusterTitle,
          groupId: null,
          origin: 'v2',
        });
      }
    }
    // Graph deep links: density-demoted + dual-impl discoverability SSOT.
    // Do not skip when settings IA lists the same People operator href —
    // settings rail cohort is not the page access cohort (see discovery gate).
    for (const node of graphDeepLinks) {
      if (v2Hrefs.has(node.href)) continue;
      if (settingsIaHrefs.has(node.href) && node.href.startsWith('/settings/')) continue;
      const item = {
        label: node.label,
        href: node.href,
        icon: iconForNavGraphDeepLink(node.href, HelpCircle),
      };
      if (
        isCommandPaletteSurfaceEnabled(item) &&
        isProgramVisible(item) &&
        isNavigationCuratedVisible(item.href) &&
        isPathAllowedForDiscovery(item.href)
      ) {
        const scopeId =
          node.scopeGroupId ??
          (node.groupId !== 'deep' && node.groupId !== 'footer' ? node.groupId : null);
        out.push({
          label: node.label,
          href: node.href,
          icon: item.icon,
          group: scopeId ? navGraphGroupLabel(scopeId) : navGraphGroupLabel(node.groupId),
          groupId: scopeId,
          keywords: node.keywords.join(' '),
          origin: 'graph',
        });
      }
    }
    for (const it of staticNavItems) {
      if (v2Hrefs.has(it.href)) continue;
      if (settingsIaHrefs.has(it.href)) continue;
      if (graphDeepHrefs.has(it.href)) continue; // graph deep links win on keywords
      const labelled = applyOrgLabel(it);
      if (
        isCommandPaletteSurfaceEnabled(labelled) &&
        isProgramVisible(labelled) &&
        isNavigationCuratedVisible(labelled.href) &&
        isPathAllowedForDiscovery(labelled.href)
      ) {
        out.push({
          label: labelled.label,
          href: labelled.href,
          icon: labelled.icon,
          group: labelled.group,
          groupId: null,
          keywords: labelled.keywords ?? graphKeywords.get(labelled.href),
          origin: 'v1',
        });
      }
    }
    return filterShiftSwapNavigationItems(out, shiftSwapsEnabled);
  }, [
    casualWorker,
    flatV2,
    v2Hrefs,
    graphKeywords,
    graphDeepLinks,
    graphDeepHrefs,
    flatSettingsIa,
    settingsIaHrefs,
    staticNavItems,
    enabledModules,
    location,
    locations,
    isPathAllowedForDiscovery,
    tenantId,
    isDemoTenant,
    visibleGoLiveScope,
    tenantHiddenNavigationPaths,
    frontlineSelfServiceOnly,
    shiftSwapsEnabled,
    payrollRebuildOn,
    user?.role,
    providerType,
    providerProgramContext,
  ]);

  // Quick actions still rely on the original v1 filter chain — they don't
  // appear in NAV_V2 and aren't candidates for the union.
  const filteredQuickActions = QUICK_ACTIONS.filter(
    (item) =>
      isCommandPaletteSurfaceEnabled(item) &&
      isProgramVisible(item) &&
      isNavigationCuratedVisible(item.href) &&
      isRoleAllowed(item.href),
  ).map(applyOrgLabel);

  // Parse the scope prefix once per query change. When scoped, the
  // displayed-item set narrows to that v2 domain; v1-only items are
  // hidden (they have no v2 group ID to match).
  const { scope, term } = useMemo(() => parseScopedQuery(search), [search]);
  const activeScopeTitle = scopeTitle(scope);
  const scopedItems = useMemo(
    () => (scope ? allAvailable.filter((i) => i.groupId === scope) : allAvailable),
    [allAvailable, scope],
  );
  const goLiveItems = useMemo(() => {
    const byHref = new Map(allAvailable.map((item) => [item.href, item]));
    const seen = new Set<string>();
    return goLiveCommandHrefs(providerType)
      .map((href) => byHref.get(href))
      .filter((item): item is NonNullable<typeof item> => {
        if (!item || seen.has(item.href)) return false;
        seen.add(item.href);
        return true;
      });
  }, [allAvailable, providerType]);
  const goLiveHrefSet = useMemo(() => new Set(goLiveItems.map((item) => item.href)), [goLiveItems]);
  const showGoLiveWork = !term && !scope && goLiveItems.length > 0;

  // Group items by their `group` field (preserves NAV_V2 domain order
  // first, then v1 categories alphabetised by Object.entries' insertion
  // order from the union loop).
  const navGroups = useMemo(
    () =>
      Object.entries(
        scopedItems.reduce<Record<string, typeof scopedItems>>((acc, item) => {
          if (showGoLiveWork && goLiveHrefSet.has(item.href)) return acc;
          (acc[item.group] ??= []).push(item);
          return acc;
        }, {}),
      ),
    [goLiveHrefSet, scopedItems, showGoLiveWork],
  );

  // ── Recents (Phase 3 — Q2 storage pattern) ────────────────────────
  // Persisted via the same useUserPreferences hook the Phase 2 sidebar
  // uses. Auth-UUID-keyed write path. Capped at 6. Pruned at render
  // time so a recent the user has lost RBAC/module access to never
  // shows. Empty by default.
  const { value: recents, setValue: setRecents } = useUserPreferences<readonly string[]>(
    'nav-v2:cmdk-recents',
    [],
  );
  const visibleHrefSet = useMemo(() => new Set(allAvailable.map((i) => i.href)), [allAvailable]);
  const renderedRecents = useMemo(
    () =>
      pruneRecents(recents, visibleHrefSet)
        .map((href) => allAvailable.find((i) => i.href === href))
        .filter((i): i is NonNullable<typeof i> => !!i),
    [recents, visibleHrefSet, allAvailable],
  );
  // Show recents in the default state (empty search, no scope) — same UX
  // as the prototype shell.jsx:283-293.
  const showRecents = !term && !scope && renderedRecents.length > 0;

  // Cmd+K to open
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        // Shell a11y: if the palette isn't open yet, capture the opener NOW (before
        // cmdk auto-focuses its input) so focus can be restored to it on close.
        // Covers ⌘K and the sidebar/mobile button (which dispatches a synthetic ⌘K).
        if (!document.querySelector('[data-command-palette-root="true"]')) {
          restoreFocusRef.current = (document.activeElement as HTMLElement | null) ?? null;
        }
        setOpen((prev) => {
          // Codex Phase 6 pass 1 P2: emit telemetry from the real
          // shortcut handler so ⌘K opens are counted alongside the
          // sidebar/mobile button opens. `dashboard-shell-v2.triggerCmdK`
          // ALSO dispatches a synthetic keydown that lands here — guard
          // with `e.isTrusted` so synthetic events (already counted at
          // the source) don't double-emit. We only fire on the OPEN
          // transition; closing via ⌘K is a different action and isn't
          // covered by the spec.
          if (e.isTrusted && !prev) {
            trackNavV2Event('nav_v2_command_palette_opened', { source: 'shortcut' });
          }
          return !prev;
        });
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  // Shell a11y (Tier-2): Escape closes the palette while it's open (the ⌘K
  // listener above only toggles ⌘K). Bound only when open so it never swallows
  // Escape for other surfaces.
  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open]);

  // Shell a11y (Tier-2): return focus to the opener on close, so keyboard users
  // aren't dumped at the top of the page. The opener is captured SYNCHRONOUSLY in
  // the open path (before cmdk auto-focuses its input — capturing here in a passive
  // effect would grab the now-focused input, which unmounts on close), so this
  // effect only handles the close transition.
  useEffect(() => {
    if (open) return;
    const restoreTarget = restoreFocusRef.current;
    if (!restoreTarget) return;
    queueMicrotask(() => restoreTarget.focus?.());
  }, [open]);

  // Shell a11y (Tier-2): explicitly move focus INTO the dialog on open. cmdk's
  // Command.Input is not reliably auto-focused, so without this the opener keeps
  // focus, the Tab trap (bound to the panel) never engages, and aria-modal has no
  // real focus containment. Focus the search input (fallback: the dialog itself).
  useEffect(() => {
    if (!open) return;
    const panel = dialogRef.current;
    if (!panel) return;
    queueMicrotask(() => {
      const input = panel.querySelector<HTMLElement>('[cmdk-input]');
      (input ?? panel).focus();
    });
  }, [open]);

  // Codex Phase 3 review MEDIUM finding: gate dynamic DB results by the
  // user's route access to /clients and /people/workers. Pre-fix the queries fired
  // for every user and the rendered groups appeared whenever the result
  // arrays were non-empty — a user without worker-register access could see staff
  // names in Cmd-K (and clicking would hit the route-level RBAC redirect).
  // Defense in depth: gate the QUERY (skip the Supabase round-trip if the
  // user can't reach the route) AND gate the RENDERED GROUP below.
  const canSearchClients =
    Boolean(activeProgramFamily) &&
    !programSelectionRequired &&
    Boolean(programContextRevision) &&
    isLaunchSurfaceVisible('/clients') &&
    isNavigationCuratedVisible('/clients') &&
    isModEnabled('/clients') &&
    isRoleAllowed('/clients');
  const canSearchStaff =
    !isComplianceTrial &&
    isLaunchSurfaceVisible('/people/workers') &&
    isNavigationCuratedVisible('/people/workers') &&
    isModEnabled('/people/workers') &&
    isRoleAllowed('/people/workers');

  // Search clients and actor-scoped workers when the user-typed term changes.
  // Phase 3 audit MEDIUM finding (term vs search): gate on `term`
  // (post-scope-strip), not raw `search`. After Phase 3, `search = "> care "`
  // would have triggered a useless DB round-trip with `ilike.%> care %`.
  useEffect(() => {
    if (!term || term.length < 2 || !user?.tenantId || (!canSearchClients && !canSearchStaff)) {
      setClients([]);
      setStaff([]);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      const sb = createClient();
      const [clientRes, workerRes] = await Promise.all([
        canSearchClients && activeProgramFamily && programContextRevision
          ? (async () => {
              const clientIds = await loadCurrentProgramClientScope(sb, {
                tenantId: user.tenantId,
                activeProgramFamily,
                admittedFirewalls: providerProgramContext?.workspace
                  ? providerProgramContext.admittedFirewalls
                  : undefined,
                authorityRevision: programContextRevision.split(':', 1)[0]!,
                asOfDate: todayLocal(timezone),
                admission: 'SETUP_CARE',
              });
              if (clientIds.length === 0) {
                return {
                  data: [] as Array<{
                    id: string;
                    first_name: string | null;
                    last_name: string | null;
                  }>,
                  error: null,
                };
              }
              let query = sb
                .from('clients')
                .select('id, first_name, last_name')
                .eq('tenant_id', user.tenantId)
                .in('id', [...clientIds])
                .or(`first_name.ilike.%${term}%,last_name.ilike.%${term}%`)
                .limit(5);
              if (isComplianceTrial) query = query.eq('status', 'ASSESSMENT');
              return query;
            })().catch(() => ({
              data: [] as Array<{
                id: string;
                first_name: string | null;
                last_name: string | null;
              }>,
              error: null,
            }))
          : Promise.resolve({
              data: [] as Array<{
                id: string;
                first_name: string | null;
                last_name: string | null;
              }>,
              error: null,
            }),
        canSearchStaff
          ? sb.rpc('list_people_workers_controlled', {
              p_filters: { search: term, page: 1, page_size: 5 },
            })
          : Promise.resolve({
              data: null,
              error: null,
            }),
      ]);
      if (!controller.signal.aborted) {
        setClients(
          (clientRes.data || [])
            .filter((c) => c.id)
            .map((c) => ({
              id: c.id as string,
              name: `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim(),
              programLabel:
                providerProgramContext?.workspace === 'HOME_COMMUNITY'
                  ? 'Home & community'
                  : activeProgramFamily
                    ? programFamilyLabel(activeProgramFamily)
                    : 'Programme',
            })),
        );
        setStaff(
          (workerRes.data &&
          typeof workerRes.data === 'object' &&
          !Array.isArray(workerRes.data) &&
          Array.isArray((workerRes.data as { items?: unknown }).items)
            ? (workerRes.data as { items: Array<{ staff_id?: unknown; display_name?: unknown }> })
                .items
            : []
          )
            .filter((worker) => typeof worker.staff_id === 'string')
            .map((worker) => ({
              id: worker.staff_id as string,
              name:
                typeof worker.display_name === 'string' ? worker.display_name : 'Name unavailable',
            })),
        );
      }
    }, 300);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [
    activeProgramFamily,
    canSearchClients,
    canSearchStaff,
    isComplianceTrial,
    programContextRevision,
    providerProgramContext,
    term,
    timezone,
    user?.tenantId,
  ]);

  const navigate = useCallback(
    (href: string) => {
      // Codex Phase 6 pass 2 P2: emit nav_v2_item_clicked so palette
      // result selections (⌘K → enter / click) are counted alongside
      // sidebar/topbar/settings-rail clicks. Without this, the command
      // palette declares itself as a source enum value but never
      // actually fires the event.
      trackNavV2Event('nav_v2_item_clicked', {
        source: 'command-palette',
        route: href,
      });
      // Record in recents BEFORE closing so the persistence write fires
      // even if the route change unmounts the palette mid-debounce. Only
      // record hrefs that exist in the current union (skip /clients/[id]
      // dynamic results so recents stay route-stable).
      if (visibleHrefSet.has(href)) {
        setRecents((prev) => appendRecent(prev ?? [], href));
      }
      setOpen(false);
      setSearch('');
      router.push(href);
    },
    [router, setRecents, visibleHrefSet],
  );

  // Shell a11y (Tier-2): keep Tab focus inside the open dialog (wrap at the ends)
  // so keyboard users can't tab into the page behind the modal.
  const trapTabKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const panel = dialogRef.current;
    if (!panel) return;
    const focusables = panel.querySelectorAll<HTMLElement>(
      'a[href],button:not([disabled]),input:not([disabled]),textarea:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])',
    );
    if (focusables.length === 0) return;
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    const active = document.activeElement;
    // Stray focus (somehow outside the dialog) → pull it back to the first focusable.
    if (active && !panel.contains(active)) {
      e.preventDefault();
      first.focus();
      return;
    }
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" data-command-palette-root="true">
      <div
        className="fixed inset-0 bg-surface-inverse/50"
        aria-hidden="true"
        onClick={() => setOpen(false)}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        tabIndex={-1}
        onKeyDown={trapTabKey}
        className="fixed left-1/2 top-[20%] -translate-x-1/2 w-full max-w-xl outline-none"
      >
        <Command
          className="rounded-xl border border-border bg-surface-base shadow-2xl overflow-hidden"
          // Phase 3: when a scope prefix is active, the input value
          // contains the `> care` prefix but the term shown to cmdk's
          // fuzzy filter should be just the user-typed portion. Override
          // the filter to ignore the prefix.
          filter={(value, searchInput) => {
            const parsed = parseScopedQuery(searchInput);
            if (!parsed.term) return 1; // match all when scope-only
            const t = parsed.term.toLowerCase();
            return value.toLowerCase().includes(t) ? 1 : 0;
          }}
        >
          <div className="flex items-center gap-2 border-b px-4">
            <Search className="w-4 h-4 text-text-muted" />
            {activeScopeTitle && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-statura-green/10 px-2 py-0.5 text-eyebrow uppercase tracking-wider text-statura-green-accessible"
                data-nav-v2-cmdk-scope={scope}
              >
                {activeScopeTitle}
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  aria-label={`Clear ${activeScopeTitle} scope`}
                  className="ml-0.5 text-statura-green-accessible/70 hover:text-statura-green-accessible"
                >
                  ×
                </button>
              </span>
            )}
            <Command.Input
              value={search}
              onValueChange={setSearch}
              placeholder={
                activeScopeTitle
                  ? `Search within ${activeScopeTitle}…`
                  : 'Search…  try "> care" to scope'
              }
              className="flex-1 py-3 text-body outline-none placeholder:text-text-muted"
            />
            <kbd className="hidden sm:inline-flex items-center gap-1 rounded border bg-surface-soft px-1.5 py-0.5 text-caption text-text-muted">
              ESC
            </kbd>
          </div>
          <Command.List className="max-h-96 overflow-y-auto p-2">
            <Command.Empty className="py-6 text-center text-body text-text-muted">
              No results found.
            </Command.Empty>

            {/* Dynamic: Clients — gated by /clients route access (codex Phase 3 MEDIUM) */}
            {canSearchClients && clients.length > 0 && (
              <Command.Group heading="Clients">
                {clients.map((c) => (
                  <Command.Item
                    key={c.id}
                    value={`client ${c.name}`}
                    onSelect={() => navigate(`/clients/${c.id}`)}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-body cursor-pointer aria-selected:bg-surface-soft"
                  >
                    <Users className="w-4 h-4 text-text-muted" />
                    {c.name}
                    <span className="ml-auto text-caption text-text-muted">{c.programLabel}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {/* Dynamic: Workers — gated by canonical worker-register route access. */}
            {canSearchStaff && staff.length > 0 && (
              <Command.Group heading="Workers">
                {staff.map((s) => (
                  <Command.Item
                    key={s.id}
                    value={`worker ${s.name}`}
                    onSelect={() => navigate(`/people/workers/${s.id}`)}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-body cursor-pointer aria-selected:bg-surface-soft"
                  >
                    <Users className="w-4 h-4 text-text-muted" />
                    {s.name}
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {/* Recents — shown only in default state (no search, no scope) */}
            {showRecents && (
              <Command.Group heading="Recent">
                {renderedRecents.map((item) => (
                  <Command.Item
                    key={`recent:${item.href}`}
                    value={`recent ${item.label}`}
                    onSelect={() => navigate(item.href)}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-body cursor-pointer aria-selected:bg-surface-soft"
                    data-nav-v2-cmdk-recent="true"
                  >
                    <item.icon className="w-4 h-4 text-text-muted" />
                    {item.label}
                    <span className="ml-auto text-caption text-text-muted">{item.group}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {/* Go-live work — provider-aware shortcuts, filtered through the same route union */}
            {showGoLiveWork && (
              <Command.Group heading="Go-live work">
                {goLiveItems.map((item) => (
                  <Command.Item
                    key={`go-live:${item.href}`}
                    value={`go live ${item.label} ${item.keywords ?? ''}`}
                    onSelect={() => navigate(item.href)}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-body cursor-pointer aria-selected:bg-surface-soft"
                    data-nav-v2-cmdk-go-live="true"
                  >
                    <item.icon className="w-4 h-4 text-text-muted" />
                    {item.label}
                    <span className="ml-auto text-caption text-text-muted">{item.group}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {/* Static: Quick Actions — hidden when a scope is active */}
            {!scope && (
              <Command.Group heading="Quick Actions">
                {filteredQuickActions.map((item) => (
                  <Command.Item
                    key={item.href}
                    value={item.label}
                    onSelect={() => navigate(item.href)}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-body cursor-pointer aria-selected:bg-surface-soft"
                  >
                    <item.icon className="w-4 h-4 text-text-muted" />
                    {item.label}
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {/* Union: NAV_V2 + v1-only routes, grouped by domain/category */}
            {navGroups.map(([group, items]) => (
              <Command.Group key={group} heading={group}>
                {items.map((item) => (
                  <Command.Item
                    key={item.href}
                    value={`${item.label} ${item.keywords ?? ''}`}
                    onSelect={() => navigate(item.href)}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-body cursor-pointer aria-selected:bg-surface-soft"
                  >
                    <item.icon className="w-4 h-4 text-text-muted" />
                    {item.label}
                  </Command.Item>
                ))}
              </Command.Group>
            ))}

            {/* Scope-hints (default state, no search): clicking pre-fills a scope prefix */}
            {!term && !scope && (
              <Command.Group heading="Search a single domain">
                {NAV_V2.map((g) => (
                  <Command.Item
                    key={`scope-hint:${g.id}`}
                    value={`scope hint ${g.title}`}
                    onSelect={() => {
                      setSearch(`> ${g.id} `);
                      // Phase 3 audit MEDIUM finding: after onSelect cmdk
                      // focuses the freshly-filtered list item, not the
                      // input. Refocus the input so keyboard users can
                      // immediately type the term in the scope they just
                      // selected. requestAnimationFrame defers past the
                      // React + cmdk focus-shift on the next paint.
                      requestAnimationFrame(() => {
                        const input = document.querySelector<HTMLInputElement>('[cmdk-input]');
                        input?.focus();
                      });
                    }}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-body cursor-pointer aria-selected:bg-surface-soft"
                  >
                    <Search className="w-4 h-4 text-text-muted" />
                    <span className="text-text-muted">&gt;</span> {g.title}
                    <span className="ml-auto text-caption text-text-muted">
                      filter to this domain
                    </span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}
          </Command.List>
          <div className="border-t px-4 py-2 text-caption text-text-muted flex gap-4">
            <span>
              <kbd className="font-mono">↑↓</kbd> Navigate
            </span>
            <span>
              <kbd className="font-mono">↵</kbd> Select
            </span>
            <span>
              <kbd className="font-mono">esc</kbd> Close
            </span>
          </div>
        </Command>
      </div>
    </div>
  );
}
